import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModalController, IonicModule } from '@ionic/angular';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Addresses } from 'src/providers/customer/addresses';
import { AddressForm, AddressComponentsModel } from 'src/providers/address-form/address-form';
import { GoogleGeocode } from 'src/providers/google-geocode/google-geocode';
import { MultipleCountryService } from 'src/providers/addresses/multiple-country.service';
import { CurrentAddress } from 'src/providers/addresses/current-address';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { AutoCompleteAddressModel } from 'src/models/autocomplete-address.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

@Component({
  selector: 'partner-signup-modal',
  templateUrl: './partner-signup-modal.component.html',
  styleUrls: ['./partner-signup-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PartnerSignupModalComponent implements OnInit {

  @Input() partnerCode: string;

  private fb = inject(FormBuilder);
  private modalController = inject(ModalController);
  private auth = inject(Auth);
  private client = inject(Client);
  private addressesService = inject(Addresses);
  private addressForm = inject(AddressForm);
  private googleGeocode = inject(GoogleGeocode);
  private multipleCountryService = inject(MultipleCountryService);
  private currentAddress = inject(CurrentAddress);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private cdr = inject(ChangeDetectorRef);

  form = this.fb.group({
    phone: ['', Validators.required],
    countryCode: ['US', Validators.required],
    address: ['', [Validators.maxLength(100), Validators.required]],
    address2: ['', Validators.maxLength(100)],
    zipcode: ['', this.multipleCountryService.getCountrySettings('US').validator],
    addressNickname: [''],
    latitude: [''],
    longitude: [''],
    city: [''],
    state: ['']
  });

  countryList: RadioButtonModel[] = this.multipleCountryService.countryRadioButtonList;
  autocompleteAddress: AutoCompleteAddressModel[] = [];
  radioButtonAddress: RadioButtonModel[] = [];
  zipCodeMask = { mask: '00000' };
  countryCode = '';

  customFlowName = '';
  customFlowImage = '';
  customFlowSmallImage = '';
  customFlowDiscount: number;
  customerResponse: any;

  loaded = false;
  isLoading = false;
  submitted = false;
  didChooseAddress = false;
  typedOnAddress = false;
  errorMessage = '';

  async ngOnInit() {
    await this.getCustomFlowData();
  }

  private async getCustomFlowData() {
    try {
      const flowData = await this.client.getCustomSBFlowData(this.partnerCode);
      this.customFlowName = flowData.settings.giver.name;
      this.customFlowImage = flowData.settings.giver.image_url;
      this.customFlowSmallImage = flowData.settings.giver.small_image_url;
      this.customFlowDiscount = flowData.settings.discount;
      localStorage.setItem('customFlowName', this.customFlowName);
      localStorage.setItem('customFlowImage', this.customFlowImage);
      localStorage.setItem('customFlowSmallImage', this.customFlowSmallImage);
      localStorage.setItem('customFlowDiscount', this.customFlowDiscount.toString());
      localStorage.setItem('customFlowReferralCode', flowData.referral_code);
      this.loaded = true;
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError('Could not load partner data. Please try again.');
      this.dismiss();
    }
  }

  updateSearch = async () => {
    this.errorMessage = '';
    try {
      this.autocompleteAddress = await this.addressForm.updateSearch(
        this.form.value.address,
        this.form.get('countryCode')?.value
      );
    } catch (err) {
      this.errorMessage = 'An error occurred when getting address information. Please type another address.';
    }
    this.radioButtonAddress = this.autocompleteAddress.map(address => ({
      value: address.value,
      viewValue: address.display,
      placeId: address.placeId
    }));
    this.typedOnAddress = true;
    this.didChooseAddress = false;
    this.cdr.markForCheck();
  }

  chooseItem = async (item: { placeId: string; value: string | number; display: string }) => {
    const zipcode = await this.addressForm.chooseItem(item);
    if (!zipcode && this.multipleCountryService.checkIfContryCodeIsUs(this.form.get('countryCode')?.value)) {
      this.errorMessage = `It looks like this address isn't detailed enough, please update it. If you still have issues please contact us in the Concierge section.`;
    }
    const address: AddressComponentsModel = await this.addressForm.getAdditonalAddressInfo(item.placeId);
    this.form.patchValue({ zipcode, ...address });
    const latLngData = await this.googleGeocode.getLatLong(item.placeId);
    this.form.patchValue(latLngData);
    this.autocompleteAddress = [];
    this.didChooseAddress = true;
    this.cdr.markForCheck();
  }

  setAddressFormControl = (item: RadioButtonModel) => {
    this.form.controls.address.setValue(item.value as string);
  }

  confirmAndSubmitAddress = async (item: any) => {
    await this.chooseItem(item);
    this.setAddressFormControl(item);
    this.radioButtonAddress = [];
    this.typedOnAddress = false;
    this.cdr.markForCheck();
    await this.addAddress();
  }

  addAddress = async () => {
    this.submitted = true;
    this.errorMessage = '';

    if (!this.didChooseAddress && this.autocompleteAddress.length === 0) {
      this.errorMessage = 'We could not validate your address with Google. Please enter an address that can be found with Google Maps.';
      this.cdr.markForCheck();
      return;
    }

    if (!this.form.valid || !this.didChooseAddress) {
      this.cdr.markForCheck();
      return;
    }

    const addressArray = this.form.value.address.split(',');
    if (!addressArray[2] && this.multipleCountryService.checkIfContryCodeIsUs(this.form.get('countryCode')?.value)) {
      this.errorMessage = `It looks like this address isn't detailed enough, please update it. If you still have issues please contact us in the Concierge section.`;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const temporaryEmail = `${this.partnerCode + (Math.random() * 9999999999999)}@testemail234234.com`;
      await this.createUser(temporaryEmail);

      const addressStateFallback: string = addressArray[2]?.trim();
      const coords = await this.googleGeocode.getLatLongWithUnit(
        { address1: addressArray[0], address2: this.form.value.address2, city: this.form.value.city, state: this.form.value.state || addressStateFallback, zip: this.form.value.zipcode },
        { latitude: this.form.value.latitude, longitude: this.form.value.longitude }
      );
      const data: any = {
        country_code: this.form.value.countryCode,
        add_state: this.form.value.state || addressStateFallback,
        city: this.form.value.city,
        address1: addressArray[0],
        address2: this.form.value.address2,
        zip: this.form.value.zipcode,
        latitude: coords.latitude,
        longitude: coords.longitude
      };

      if (this.form.value.addressNickname) {
        data['address_name'] = this.form.value.addressNickname;
      }

      const address = await this.addressesService.createAddress(data);
      localStorage.setItem('hasAtLeastOneAddress', 'true');
      this.currentAddress.addressId = address.id;
      localStorage.setItem('region_id', address.region_id);
      await this.storage.save('isSelfBooking', true);

      this.modalController.dismiss({ address });
    } catch (err: any) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async createUser(temporaryEmail: string) {
    await this.authLogin(temporaryEmail, 'signUp');
    await this.getCustomers(temporaryEmail);
    this.auth.setSession();
    await this.authLogin(temporaryEmail, 'signUpPassword');
  }

  private async authLogin(temporaryEmail: string, scope: string) {
    const credentials = {
      username: temporaryEmail,
      password: 'password'
    };
    await this.auth.login(credentials, scope);
  }

  private async getCustomers(temporaryEmail: string) {
    const customersObj = {
      customer: {
        booked_via: 'Self Booking',
        email: temporaryEmail,
        first_name: 'Client',
        last_name: '1',
        password: 'password',
        account_type: 'regular',
        account_name: ''
      }
    };
    this.customerResponse = await this.client.getCustomers(customersObj);
    localStorage.setItem('customer_id', this.customerResponse.id);
  }

  changeCountrySelected = (countryCodeSelected: string) => {
    const validatorObject = this.multipleCountryService.getCountrySettings(countryCodeSelected);
    this.form.get('zipcode')?.setValidators(validatorObject?.validator);
    this.zipCodeMask = validatorObject?.mask;
    this.form.patchValue({
      address: null,
      address2: null,
      zipcode: null
    });
    this.cdr.markForCheck();
  }

  onCountryCodeChange = (countryCode: string) => {
    this.countryCode = countryCode.replace('+', '');
  }

  goToLoginPage = () => {
    this.modalController.dismiss();
    this.navCtrl.navigateForward('login');
  }

  dismiss = () => {
    this.modalController.dismiss();
  }
}
